<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import type { Swiper as SwiperCore } from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'
import {PhCaretLeft, PhCaretRight, PhArrowsOut, PhX} from '@phosphor-icons/vue'
import { resolveMediaUrl } from '@/utils/media'
import ListingImage from '@/components/listings/ListingImage.vue'

interface Props {
  images: string[]
  initialIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  initialIndex: 0,
  images: () => [] as string[],
})

const activeIndex = ref(props.initialIndex)
const showFullscreen = ref(false)
const fullscreenIndex = ref(props.initialIndex)

// Swiper instances are kept non-reactive on purpose.
let mainSwiper: SwiperCore | null = null
let fullscreenSwiper: SwiperCore | null = null

const canLoop = computed(() => props.images.length > 2)

// swiper/vue ships overly strict prop typings for module params.
const paginationConfig: any = { clickable: true }

const visibleThumbs = computed(() => {
  const total = props.images.length
  const current = activeIndex.value
  const start = Math.max(0, current - 2)
  const end = Math.min(total, start + 5)
  return props.images.slice(start, end)
})

const thumbStartIndex = computed(() => {
  const total = props.images.length
  const current = activeIndex.value
  return Math.max(0, Math.min(current - 2, total - 5))
})

function onMainSwiperReady(swiper: SwiperCore) {
  mainSwiper = swiper
}

function onFullscreenSwiperReady(swiper: SwiperCore) {
  fullscreenSwiper = swiper
}

function slideToIndex(swiper: SwiperCore | null, index: number) {
  if (!swiper) return
  if (canLoop.value) {
    swiper.slideToLoop(index)
  } else {
    swiper.slideTo(index)
  }
}

function onSlideChange(swiper: SwiperCore) {
  activeIndex.value = swiper.realIndex
}

function onThumbClick(index: number) {
  slideToIndex(mainSwiper, thumbStartIndex.value + index)
}

function prevImage() {
  mainSwiper?.slidePrev()
}

function nextImage() {
  mainSwiper?.slideNext()
}

function openFullscreen(index: number) {
  fullscreenIndex.value = index
  showFullscreen.value = true
  nextTick(() => {
    slideToIndex(fullscreenSwiper, index)
  })
}

function closeFullscreen() {
  showFullscreen.value = false
}

function onFullscreenSlideChange(swiper: SwiperCore) {
  fullscreenIndex.value = swiper.realIndex
}

function fullscreenSwiperPrev() {
  fullscreenSwiper?.slidePrev()
}

function fullscreenSwiperNext() {
  fullscreenSwiper?.slideNext()
}

function slideToFullscreen(index: number) {
  slideToIndex(fullscreenSwiper, index)
}

function onKeydown(e: KeyboardEvent) {
  if (!showFullscreen.value) return

  if (e.key === 'Escape') {
    closeFullscreen()
  } else if (e.key === 'ArrowLeft') {
    fullscreenSwiper?.slidePrev()
  } else if (e.key === 'ArrowRight') {
    fullscreenSwiper?.slideNext()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div class="relative">
    <Swiper
      :modules="[Pagination]"
      :initial-slide="props.initialIndex"
      :space-between="0"
      :speed="300"
      :loop="canLoop"
      :pagination="paginationConfig"
      @swiper="onMainSwiperReady"
      @slide-change="onSlideChange"
      class="listing-gallery-swiper relative aspect-video rounded-xl overflow-hidden bg-surface"
    >
      <SwiperSlide v-for="(img, i) in props.images" :key="i" class="relative">
        <ListingImage :src="img" :alt="`Image ${i + 1}`" @click="openFullscreen(i)" />
      </SwiperSlide>

      <button
        v-if="props.images.length > 1"
        class="swiper-button-prev absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white transition-all hover:bg-black/70 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400 z-10"
        aria-label="Previous image"
        @click.prevent="prevImage"
      >
        <PhCaretLeft class="w-5 h-5" />
      </button>

      <button
        v-if="props.images.length > 1"
        class="swiper-button-next absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white transition-all hover:bg-black/70 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400 z-10"
        aria-label="Next image"
        @click.prevent="nextImage"
      >
        <PhCaretRight class="w-5 h-5" />
      </button>

      <button
        v-if="props.images.length > 0"
        class="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white transition-all hover:bg-black/70 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400"
        aria-label="View fullscreen"
        @click="openFullscreen(activeIndex)"
      >
        <PhArrowsOut class="w-5 h-5" />
      </button>

      <div
        v-if="props.images.length > 3"
        class="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 pb-8"
        role="tablist"
        aria-label="Image thumbnails"
      >
        <button
          v-for="(img, i) in visibleThumbs"
          :key="thumbStartIndex + i"
          role="tab"
          :aria-selected="thumbStartIndex + i === activeIndex"
          :aria-label="`View image ${thumbStartIndex + i + 1}`"
          @click="onThumbClick(i)"
          class="w-16 h-9 rounded-lg overflow-hidden border-2 transition-all relative shrink-0"
          :class="thumbStartIndex + i === activeIndex
            ? 'border-cyan-400 scale-105 z-10'
            : 'border-transparent opacity-60 hover:opacity-100'"
        >
          <img
            :src="resolveMediaUrl(img)"
            :alt="`Thumbnail ${thumbStartIndex + i + 1}`"
            class="w-full h-full object-cover"
            loading="lazy"
          />
        </button>
      </div>
    </Swiper>

    <div
      v-if="showFullscreen"
      class="fixed inset-0 z-50 bg-black flex items-center justify-center"
      @click="closeFullscreen"
      role="dialog"
      aria-modal="true"
      aria-label="Fullscreen image viewer"
    >
      <Swiper
        :initial-slide="fullscreenIndex"
        :space-between="0"
        :speed="300"
        :loop="canLoop"
        @swiper="onFullscreenSwiperReady"
        @slide-change="onFullscreenSlideChange"
        class="w-full h-full max-w-[90vw] max-h-[90vh]"
      >
        <SwiperSlide v-for="(img, i) in props.images" :key="i" class="relative flex items-center justify-center">
          <img
            :src="resolveMediaUrl(img)"
            :alt="`Image ${i + 1} of ${props.images.length}`"
            class="max-w-full max-h-full object-contain"
          />
        </SwiperSlide>
      </Swiper>

      <button
        class="absolute top-4 right-4 w-12 h-12 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white transition-all hover:bg-black/70 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400"
        aria-label="Close fullscreen"
        @click="closeFullscreen"
      >
        <PhX class="w-6 h-6" />
      </button>

      <button
        v-if="props.images.length > 1"
        class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white transition-all hover:bg-black/70 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400"
        aria-label="Previous image"
        @click="fullscreenSwiperPrev"
      >
        <PhCaretLeft class="w-6 h-6" />
      </button>

      <button
        v-if="props.images.length > 1"
        class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white transition-all hover:bg-black/70 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400"
        aria-label="Next image"
        @click="fullscreenSwiperNext"
      >
        <PhCaretRight class="w-6 h-6" />
      </button>

      <div class="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10" role="tablist">
        <button
          v-for="(img, i) in props.images"
          :key="i"
          role="tab"
          :aria-selected="i === fullscreenIndex"
          :aria-label="`View image ${i + 1}`"
          @click="slideToFullscreen(i)"
          class="w-16 h-9 rounded-lg overflow-hidden border-2 transition-all relative shrink-0"
          :class="i === fullscreenIndex
            ? 'border-cyan-400 scale-105 z-10'
            : 'border-transparent opacity-60 hover:opacity-100'"
        >
          <img
            :src="resolveMediaUrl(img)"
            :alt="`Thumbnail ${i + 1}`"
            class="w-full h-full object-cover"
            loading="lazy"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style>
.listing-gallery-swiper .swiper-pagination {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
  z-index: 10;
}

.listing-gallery-swiper .swiper-pagination-bullet {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.5);
  transition: all 0.2s ease-out;
}

.listing-gallery-swiper .swiper-pagination-bullet-active {
  background: #22d3ee;
  width: 20px;
}
</style>